import { useEffect } from 'react'
import { useRouter } from 'next/router'
import classNames from 'classnames'
import { Field, Form, Formik } from 'formik'
import PropTypes from 'prop-types'
import styled from 'styled-components'

import AppleSignInButton from 'components/Atoms/AppleSignInButton'
import BulletPoint from 'components/Atoms/BulletPoint'
import Button from 'components/Atoms/Button'
// import { EdcLoginButton } from 'components/Atoms/EdcLoginButton'
// import FacebookButton from 'components/Atoms/FacebookButton'
import Text from 'components/Atoms/Text'
import TextDivisor from 'components/Atoms/TextDivisor'
import { Checkbox, PhoneField, TextField } from 'components/Molecules/Form'
import PhoneDisclaimer from 'components/Molecules/PhoneDisclaimer'
import { isCitifydApp } from 'helpers/application'
import isLightMode from 'helpers/isLightMode'
import { Trans } from 'i18n-web/i18next'

import validationSchema from './validationSchema'

const Component = styled.div`
  margin-bottom: 20px;
  .phoneDisclaimer {
    margin-top: -8px;
    margin-bottom: 15px;
  }
  .bold {
    font-weight: bold;
  }
  .extraMargin {
    margin-bottom: 12px;
  }
`

const Fields = styled.div`
  margin-top: ${props => (props.extraMargin ? '20px' : 0)};
`

const SocialMediaAlert = styled(Text)`
  margin: 30px 0;
  padding: 12px 18px;
  font-size: ${props => props.theme.font.size.mobile.medium};
  border-radius: 4px;
  &.apple {
    color: ${props => props.theme.palette.grayDark};
    background-color: ${props => props.theme.palette.white};
  }
  &.facebook {
    color: ${props => props.theme.palette.white};
    background-color: ${props => props.theme.palette.facebookBlue};
  }
  @media (min-width: ${props => props.theme.flexboxgrid.breakpoints.sm}rem) {
    font-size: ${props => props.theme.font.size.desktop.regular};
  }
`

const TermsMessage = styled(Trans)`
  .link {
    color: ${props => props.theme.palette.grayDark};
  }
`

const TermsErrorMessage = styled(Text)`
  margin-bottom: 20px;
`

const SubmitButton = styled(Button)`
  margin-top: 20px;
`

const SignUp = ({
  country,
  error,
  facebookToken,
  formikRef,
  handlePasswordOnKeyUp,
  handleSubmit,
  hasNamePronunciation,
  initialValues,
  onFocusField,
  onTermsClick,
  passwordRules,
  setPhoneToken,
  showSaveCardCheckbox,
  application,
  setFieldValue,
  t,
}) => {
  const router = useRouter()
  const { query } = router
  const {
    apple_login_token: appleLoginToken,
    email_available: appleEmailAvailable,
    name_available: appleNameAvailable,
  } = query

  const composedInitialValues = {
    ...initialValues,
    ...(appleEmailAvailable && { email: appleEmailAvailable || '01' }),
    ...(appleNameAvailable && { name: appleNameAvailable || '02' }),
  }

  useEffect(() => {
    if (appleEmailAvailable && setFieldValue) {
      setFieldValue('email', appleEmailAvailable, false)
    }
  }, [appleEmailAvailable])

  useEffect(() => {
    if (appleNameAvailable && setFieldValue) {
      setFieldValue('name', appleNameAvailable, false)
    }
  }, [appleNameAvailable])

  return (
    <Formik
      initialValues={composedInitialValues}
      onSubmit={handleSubmit}
      ref={ref => formikRef(ref)}
      render={props => {
        const {
          isSubmitting,
          handleChange,
          setStatus,
          touched,
          errors,
          values,
        } = props

        return (
          <Component>
            {!facebookToken &&
              !appleLoginToken &&
              isCitifydApp(application) && (
                <>
                  <TextDivisor text={t('signUp.socialMedia')} />
                  <AppleSignInButton
                    skipLoginFlow={Boolean(
                      process.env.NEXT_PUBLIC_ENABLE_PAY_BY_SIGNAGE_ONLY
                    )}
                    className="full button"
                    callbackRedirection={router.asPath}>
                    {t('common.button.continueWithApple')}
                  </AppleSignInButton>
                  {/* <FacebookButton
                    redirectTo={router.asPath}
                    className="full button">
                    {t('common.button.continueWithFacebook')}
                  </FacebookButton> */}
                  {/* <EdcLoginButton text={t('common.button.continueWithEvents')} /> */}
                  <TextDivisor text={t('signUp.or')} />
                </>
              )}
            {!facebookToken && appleLoginToken && (
              <SocialMediaAlert className="apple semiBold">
                {t('signUp.completeWithApple')}
              </SocialMediaAlert>
            )}
            {facebookToken && !appleLoginToken && (
              <SocialMediaAlert className="facebook semiBold">
                {t('signUp.completeWithFacebook')}
              </SocialMediaAlert>
            )}
            <Form>
              <Fields extraMargin={!isCitifydApp(application)}>
                <Field
                  component={PhoneField}
                  country={country}
                  disabled={isSubmitting && !error}
                  label={t('signUp.form.phoneNumber.label')}
                  name="phoneNumber"
                  onFocus={() => {
                    onFocusField(setStatus)
                    setPhoneToken(null)
                  }}
                />
                <PhoneDisclaimer
                  application={application}
                  className="phoneDisclaimer"
                />
                <Field
                  component={TextField}
                  disabled={isSubmitting && !error}
                  label={t('signUp.form.name.label')}
                  name="name"
                  onFocus={() => onFocusField(setStatus)}
                />
                <Field
                  component={TextField}
                  disabled={isSubmitting && !error}
                  label={t('signUp.form.email.label')}
                  name="email"
                  onFocus={() => onFocusField(setStatus)}
                />
                {hasNamePronunciation && (
                  <Field
                    component={TextField}
                    disabled={isSubmitting && !error}
                    label={t('signUp.form.namePronunciation.label')}
                    name="namePronunciation"
                    onFocus={() => onFocusField(setStatus)}
                  />
                )}
                {!facebookToken && !appleLoginToken && (
                  <>
                    <Field
                      component={TextField}
                      disabled={isSubmitting && !error}
                      label={t('signUp.form.password.label')}
                      name="password"
                      onFocus={() => onFocusField(setStatus)}
                      onKeyUp={handlePasswordOnKeyUp}
                      type="password"
                    />
                    <BulletPoint
                      isChecked={
                        passwordRules?.missingFeatures &&
                        !passwordRules?.missingFeatures.includes('minimum')
                      }>
                      {t('signUp.form.password.requirements.minimum')}
                    </BulletPoint>
                    <Field
                      component={TextField}
                      disabled={isSubmitting && !error}
                      label={t('signUp.form.confirmPassword.label')}
                      name="confirmPassword"
                      onFocus={() => onFocusField(setStatus)}
                      type="password"
                    />
                  </>
                )}
                <Checkbox
                  checked={values.acceptedTerms}
                  className={classNames({
                    error: errors.acceptedTerms && touched.acceptedTerms,
                  })}
                  label={
                    <TermsMessage i18nKey="signUp.form.terms.label">
                      <Text
                        as="span"
                        className="tiny bold link"
                        onClick={() => onTermsClick()}
                        target="_blank"></Text>
                    </TermsMessage>
                  }
                  name="acceptedTerms"
                  onClick={handleChange}
                  value="agree"
                />
                {errors.acceptedTerms && touched.acceptedTerms && (
                  <TermsErrorMessage className="small error">
                    {errors.acceptedTerms}
                  </TermsErrorMessage>
                )}
                {showSaveCardCheckbox && (
                  <>
                    <Checkbox
                      checked={values.saveCards}
                      label={
                        <Text className="tiny">
                          {t('signUp.form.saveCards.label')}
                        </Text>
                      }
                      name="saveCards"
                      onClick={handleChange}
                      value="agree"
                    />
                  </>
                )}
                <SubmitButton
                  className={classNames('full', {
                    highlight: isLightMode(application),
                    contrastText: !isLightMode(application),
                  })}
                  disabled={isSubmitting}
                  type="submit">
                  {t('signUp.form.button').toUpperCase()}
                </SubmitButton>
              </Fields>
            </Form>
          </Component>
        )
      }}
      validationSchema={() =>
        validationSchema(
          country,
          hasNamePronunciation,
          facebookToken,
          appleLoginToken,
          appleNameAvailable,
          passwordRules
        )
      }
    />
  )
}

SignUp.propTypes = {
  country: PropTypes.string,
  error: PropTypes.string,
  errors: PropTypes.object,
  facebookToken: PropTypes.string,
  formikRef: PropTypes.func,
  handleChange: PropTypes.func,
  handlePasswordOnKeyUp: PropTypes.func,
  handleSubmit: PropTypes.func,
  hasNamePronunciation: PropTypes.bool,
  initialValues: PropTypes.object,
  isSubmitting: PropTypes.bool,
  onFocusField: PropTypes.func,
  onTermsClick: PropTypes.func,
  passwordRules: PropTypes.object,
  setPhoneToken: PropTypes.func,
  setStatus: PropTypes.func,
  t: PropTypes.func,
  touched: PropTypes.bool,
  showSaveCardCheckbox: PropTypes.bool,
  values: PropTypes.object,
  application: PropTypes.string,
  setFieldValue: PropTypes.func,
}

export default SignUp
